package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 복구 가능한 콜러블 실패. Firebase Functions의 `HttpsError` 코드에 1:1 대응한다.
 *
 * **왜 예외가 아니라 값인가:** Swift가 Kotlin `suspend` 계약을 구현·호출할 때
 * 경계를 넘는 uncaught Kotlin 예외는 복구 불가능한 종료다. 기존 [AuthResult.errorMessage] 패턴을
 * 그대로 확장해 복구 가능한 실패는 전부 반환값으로 표현한다.
 * 코루틴 취소(`CancellationException`)만 예외로 통과시킨다.
 */
@Serializable
enum class CommandFailure {
    UNAUTHENTICATED,
    PERMISSION_DENIED,
    NOT_FOUND,
    INVALID_ARGUMENT,
    FAILED_PRECONDITION,
    ALREADY_EXISTS,
    RESOURCE_EXHAUSTED,
    UNAVAILABLE,
    INTERNAL,
}

/**
 * 단일 쓰기 콜러블의 결과.
 *
 * 성공이면 [failure]가 null이고 서버가 만든 문서 id가 [entityId]에 온다.
 */
@Serializable
data class CommandResult(
    val entityId: String? = null,
    val failure: CommandFailure? = null,
    /** 사용자가 해결할 수 있는 한국어 문장. 공급자 코드를 그대로 노출하지 않는다(MVP §9). */
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

/**
 * 일괄 처리 결과(v4 `2명 출석 처리`).
 *
 * 부분 성공 의미론은 아직 확정되지 않았다(계획 §13 Q19). 그래서 전체 실패([failure])와
 * 건별 결과([results])를 모두 담아 어느 쪽으로 확정되든 계약을 바꾸지 않아도 되게 한다.
 */
@Serializable
data class BatchCommandResult(
    val results: List<CommandResult> = emptyList(),
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && results.all { it.isSuccess }

    val failureCount: Int get() = results.count { !it.isSuccess }
}

/** v4 대시보드 우하단 `전달 71 · 재시도 2`. rules로는 못 읽어서 콜러블 경유다(§S3). */
@Serializable
data class NotificationSummary(
    val academyId: String,
    val deliveredCount: Int = 0,
    val retryingCount: Int = 0,
    val failedCount: Int = 0,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null
}

// ---- 콜러블 입력 ----
//
// 데이터 클래스의 기본값은 ObjC로 노출되지 않아도 무방하다(Swift는 전 인자를 채워 생성한다).
// 경계 인터페이스 메서드에는 기본값을 쓰지 않는다.

@Serializable
data class AcademyApplicationRequest(
    val academyName: String,
    val ownerName: String,
    val phone: String,
    val address: String,
    val description: String = "",
)

/** 학원 기본 정보 수정. 해당 학원의 ADMIN만 실행할 수 있다. */
@Serializable
data class UpdateAcademyRequest(
    val academyId: String,
    val name: String,
    val address: String,
    val phone: String,
    val description: String = "",
)

/**
 * 학원 개설 신청 상태.
 *
 * Functions가 실제로 쓰는 값은 둘뿐이다(`roles.ts` — 제출 시 `PENDING`, 승인 시 `APPROVED`).
 * 반려 경로는 아직 구현되어 있지 않으므로 `REJECTED`를 미리 만들지 않는다.
 */
@Serializable
enum class AcademyApplicationStatus(val displayName: String) {
    PENDING("승인 대기"),
    APPROVED("승인됨"),
}

/**
 * `edubridge_academyApplications/{id}` 읽기 모델. SUPER_ADMIN 승인 큐(`/app/admin/applications`)가 쓴다.
 *
 * rules는 SUPER_ADMIN 또는 **신청자 본인**에게만 read를 연다 — 신청자가 자기 신청의
 * 처리 상태를 확인할 수 있어야 하기 때문이다(S9-2·S9-3 경로).
 */
@Serializable
data class AcademyApplication(
    /** 문서 id. `approveAcademyApplication`에 넘기는 `applicationId`다. */
    val id: String,
    val academyName: String,
    val applicantUid: String,
    val contactPhone: String? = null,
    val status: AcademyApplicationStatus,
    val createdAt: String,
    /** 승인 후에만 채워진다 — 만들어진 학원 문서 id. */
    val academyId: String? = null,
    val decidedBy: String? = null,
    val decidedAt: String? = null,
)

@Serializable
data class CreateClassRequest(
    val academyId: String,
    val className: String,
    val subject: String,
    val schedule: ClassSchedule,
    val teacherUserId: String? = null,
)

/** 반 수업 정보 수정. 해당 학원의 EDITOR 이상이 수정할 수 있다. */
@Serializable
data class UpdateClassRequest(
    val academyId: String,
    val classId: String,
    val className: String,
    val subject: String,
    val schedule: ClassSchedule,
)

@Serializable
data class CreateStudentRequest(
    val academyId: String,
    val name: String,
    val grade: String,
    val phone: String? = null,
    val guardianContacts: List<GuardianContact> = emptyList(),
)

/**
 * 학생 정보 수정(§2.5). 호출자는 해당 학원에서 EDITOR 이상이어야 한다.
 * [guardianContacts] 변경은 개인정보 변경이므로 감사 로그 대상이다.
 */
@Serializable
data class UpdateStudentRequest(
    val academyId: String,
    val studentId: String,
    val name: String,
    val grade: String,
    val phone: String? = null,
    val guardianContacts: List<GuardianContact> = emptyList(),
)

/**
 * 문제 은행 과제의 출제 조건 **입력**. [ProblemBankSpec]와 달리 `combinationKey`가 없다 —
 * 그 키는 EduMint 문제 은행의 것이라 서버(`assignments.ts`)가 조합한다.
 */
@Serializable
data class ProblemBankSelection(
    /** "국어" | "수학". */
    val subject: String,
    /** "초1".."고3". */
    val grade: String,
    /** "쉬움" | "보통" | "어려움". */
    val difficulty: String,
    /** "객관식" | "OX". 수학이면 서버가 무시한다. */
    val questionType: String = "",
    /** 5 | 10 | 20. */
    val questionCount: Int,
)

/**
 * 과제 등록. 호출자는 EDITOR 이상이어야 한다.
 *
 * [classId]가 null이면 학원 전체 과제다. [kind]가 [AssignmentKind.PROBLEM_BANK]면
 * [problemBank]가 있어야 하고, 없으면 서버가 `INVALID_ARGUMENT`로 거부한다.
 */
@Serializable
data class CreateAssignmentRequest(
    val academyId: String,
    val title: String,
    /** `YYYY-MM-DD` — 시각이 아니라 학원 달력의 마감일이다. */
    val dueDate: String,
    val classId: String? = null,
    val kind: AssignmentKind = AssignmentKind.NOTICE,
    val problemBank: ProblemBankSelection? = null,
)

/**
 * 과제 수정. [title]·[dueDate] 중 적어도 하나는 null이 아니어야 한다([UpdateNoticeRequest]와 같은 규칙).
 *
 * 문제 은행 선택은 바꿀 수 없다 — 제출 결과가 그 조합으로 뽑힌 문항을 채점한 값이라,
 * 조합을 갈아 끼우면 남은 결과가 아무것도 설명하지 못한다. 문항을 바꾸려면 새 과제다.
 */
@Serializable
data class UpdateAssignmentRequest(
    val academyId: String,
    val assignmentId: String,
    val title: String? = null,
    val dueDate: String? = null,
)

/**
 * 수동 출결 처리(§S2). `classId`·`serviceDate`가 없으면 한 학생이 하루 3개 반이어도
 * 한 레코드로 뭉개진다 — 결정적 id `{serviceDate}_{classId}_{studentId}`의 재료다.
 */
@Serializable
data class RecordAttendanceRequest(
    val academyId: String,
    val classId: String,
    val studentId: String,
    val serviceDate: String,
    val status: AttendanceStatus,
    /** 같은 키로 두 번 호출해도 이벤트는 1건이어야 한다(S2 단언 ①). */
    val idempotencyKey: String,
    val occurredAt: String? = null,
)

/** 정정. 삭제가 아니라 AMEND 이벤트를 append하고 `previousEventId`로 연결한다(§3.5). */
@Serializable
data class AmendAttendanceRequest(
    val academyId: String,
    val previousEventId: String,
    val status: AttendanceStatus,
    val reason: String,
    val idempotencyKey: String,
)
